import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Hash, Pencil, Plus, Trash2, X } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchPlurals,
  createPlural,
  updatePlural,
  deletePlural,
  LOC_STR as S,
  type PluralRow,
  type PluralCat,
} from './localizationExtraApi'

/**
 * A22.06 — Pluralizatsiya qoidalari. CLDR ko'plik-kategoriyalari (til bo'yicha) —
 * tarjimonlar uchun ma'lumotnoma. Endi ADMIN-boshqariladigan JONLI reyestr
 * (GET/POST/PUT/DELETE /admin/localization/plurals; uz/ru/en standart CLDR bilan
 * urug'langan) — avvalgi statik massiv o'rniga. Qo'shish/tahrir/o'chirish serverga
 * yoziladi (audited).
 */

/** categories → tahrirlanadigan matn (har qatorда `category | misol | qoida`). */
function catsToText(cats: PluralCat[]): string {
  return cats.map((c) => [c.category, c.example, c.rule].join(' | ')).join('\n')
}

/** tahrir-matnni categories massiviga qaytaradi (bo'sh/kategoriyasiz qatorlar tashlanadi). */
function textToCats(text: string): PluralCat[] {
  const out: PluralCat[] = []
  for (const line of text.split('\n')) {
    const parts = line.split('|').map((p) => p.trim())
    const category = parts[0] ?? ''
    if (!category) continue
    out.push({ category, example: parts[1] ?? '', rule: parts[2] ?? '' })
  }
  return out
}

export function LocalizationPlurals() {
  const t = useT()
  const { toast } = useToast()
  const [rows, setRows] = useState<PluralRow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((x) => x + 1), [])

  // Create/edit form. editingId=null → create; a number → editing that row.
  const [editingId, setEditingId] = useState<number | null>(null)
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [catsText, setCatsText] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchPlurals(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const resetForm = () => {
    setEditingId(null)
    setCode('')
    setName('')
    setCatsText('')
  }

  const startEdit = (r: PluralRow) => {
    setEditingId(r.id)
    setCode(r.lang_code)
    setName(r.lang_name)
    setCatsText(catsToText(r.categories))
  }

  const submit = async () => {
    if (busy) return
    const cats = textToCats(catsText)
    if (editingId === null && !code.trim()) return
    setBusy(true)
    try {
      if (editingId === null) {
        await createPlural({ lang_code: code.trim(), lang_name: name.trim(), categories: cats })
        toast(S.pluralAdded, { tone: 'success' })
      } else {
        await updatePlural(editingId, { lang_name: name.trim(), categories: cats })
        toast(S.pluralUpdated, { tone: 'success' })
      }
      resetForm()
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (r: PluralRow) => {
    try {
      await deletePlural(r.id)
      if (editingId === r.id) resetForm()
      toast(S.pluralRemoved, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        title={t.loc.pluralsTitle}
        screenId="A22.06"
        actions={
          <Link
            to="/localization"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.loc.title}
          </Link>
        }
      />

      {/* Add / edit form */}
      <div className="border-b border-border-subtle p-4">
        <p className="mb-2 text-sm text-secondary">{t.loc.pluralsNote}</p>
        <div className="flex flex-wrap items-start gap-2">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={S.pluralCode}
            disabled={editingId !== null}
            className="w-28 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent disabled:opacity-60"
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={S.pluralName}
            className="w-40 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary outline-none focus:border-accent"
          />
          <textarea
            value={catsText}
            onChange={(e) => setCatsText(e.target.value)}
            placeholder={S.pluralCatsHint}
            rows={3}
            className="min-w-[240px] flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 font-mono text-xs text-primary outline-none focus:border-accent"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy || (editingId === null && !code.trim())}
              className="flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm text-white disabled:opacity-50"
            >
              <Plus size={14} />
              {editingId === null ? S.pluralAdd : S.pluralSave}
            </button>
            {editingId !== null && (
              <button
                type="button"
                onClick={resetForm}
                className="flex items-center gap-1 rounded-md border border-border-default px-3 py-1.5 text-sm text-secondary hover:text-primary"
              >
                <X size={14} />
                {S.pluralCancel}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-4">
        {err ? (
          <ErrorState code={err.code} onRetry={reload} />
        ) : !rows ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-40 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className="rounded-md border border-dashed border-border-default p-6 text-center text-sm text-tertiary">
            {S.pluralNone}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((l) => (
              <div key={l.id} className="rounded-lg border border-border-default bg-surface-1 p-4">
                <div className="mb-3 flex items-center gap-2">
                  <Hash size={15} className="text-accent" />
                  <span className="font-medium text-primary">{l.lang_name || l.lang_code}</span>
                  <span className="font-mono text-xs text-tertiary">{l.lang_code}</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => startEdit(l)}
                      title={S.pluralEdit}
                      className="text-tertiary hover:text-accent"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(l)}
                      title={S.pluralRemoved}
                      className="text-tertiary hover:text-danger"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {l.categories.length === 0 ? (
                    <p className="text-xs text-tertiary">{l.categories.length} {S.pluralCats}</p>
                  ) : (
                    l.categories.map((c) => (
                      <div key={c.category} className="flex items-start gap-2 text-sm">
                        <span className="w-16 shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-center font-mono text-xs text-accent">
                          {c.category}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-secondary">{c.example || '—'}</span>
                          {c.rule && c.rule !== '—' && (
                            <span className="block font-mono text-xs text-tertiary">{c.rule}</span>
                          )}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
